import React, { useEffect, useState } from "react";
import api from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { fmtDate, fmtTime, todayISO } from "@/lib/tps";
import { StatusBadge } from "@/components/StatusBadge";
import { Link } from "react-router-dom";
import { ChefHat } from "lucide-react";

const STAGES = ["New", "Confirmed", "In Production", "Ready", "Out for Delivery"];

export default function TodayProduction() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const t = todayISO();
    api.get(`/orders?start=${t}&end=${t}`).then((r) => setOrders(r.data.filter((o) => !["Cancelled", "Lost"].includes(o.status))));
  }, []);

  const shown = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  return (
    <div className="space-y-4 animate-fade-in" data-testid="production-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[#B86F77]">
            <ChefHat className="h-5 w-5" />
            <span className="text-xs uppercase tracking-widest">Kitchen View</span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl text-[#141E30] mt-1">Today's Production</h1>
          <div className="text-[#4B5563] text-base">{fmtDate(todayISO())} · {orders.length} order{orders.length !== 1 ? "s" : ""}</div>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <FilterBtn active={filter === "all"} onClick={() => setFilter("all")} label={`All (${orders.length})`} testId="prod-filter-all" />
          {STAGES.map((s) => {
            const n = orders.filter((o) => o.status === s).length;
            return (
              <FilterBtn key={s} active={filter === s} onClick={() => setFilter(s)} label={`${s} (${n})`} testId={`prod-filter-${s.toLowerCase().replace(/\s+/g, "-")}`} />
            );
          })}
        </div>
      </div>

      {shown.length === 0 ? (
        <Card className="border-[#E8E3DF]"><CardContent className="p-10 text-center text-[#4B5563]">No orders here.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {shown.map((o) => {
            const it = o.items?.[0] || {};
            return (
              <Link key={o.id} to={`/orders/${o.id}`} data-testid={`prod-card-${o.order_id}`}>
                <Card className="border-[#E8E3DF] hover:border-[#B86F77] transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-heading text-lg md:text-xl text-[#B86F77]">{o.order_id}</span>
                          <StatusBadge status={o.status} />
                        </div>
                        <div className="font-heading text-2xl md:text-3xl text-[#141E30] mt-1 leading-tight">{it.theme || "Custom Cake"}</div>
                        <div className="text-base md:text-lg mt-1 text-[#141E30]">
                          {it.weight} · {it.tier} · {it.flavour}
                        </div>
                        {it.cake_message && (
                          <div className="mt-2 text-base bg-[#FCF9F9] rounded-md p-2 border border-[#E8E3DF]">
                            <span className="text-[10px] uppercase tracking-wider text-[#4B5563] block">Message on cake</span>
                            <span className="font-medium">"{it.cake_message}"</span>
                          </div>
                        )}
                        {it.customization_notes && (
                          <div className="mt-2 text-sm text-[#4B5563] whitespace-pre-wrap">{it.customization_notes}</div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">Required</div>
                        <div className="font-heading text-2xl md:text-3xl text-[#141E30]">{fmtTime(o.required_time)}</div>
                        <div className="text-xs text-[#4B5563] mt-1">{o.delivery_method}</div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-[#E8E3DF] flex items-center justify-between">
                      <div className="text-sm text-[#4B5563]">{o.customer_name} · {o.customer_phone}</div>
                      {o.final_reference_image && (
                        <img src={o.final_reference_image} alt="" className="h-12 w-12 object-cover rounded-md border border-[#E8E3DF]" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FilterBtn({ active, onClick, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`h-8 px-3 rounded-md text-sm border transition-colors ${
        active ? "bg-[#B86F77] text-white border-[#B86F77]" : "bg-white text-[#141E30] border-[#E8E3DF] hover:bg-[#FCF9F9]"
      }`}
    >{label}</button>
  );
}
